import { QueryConstraint, where } from "firebase/firestore";
import { logError } from "../../utils/customLogger";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";

export interface OrderAssignmentConfiguration {
  id?: string;
  auth_status: string[];
  capacity: number;
  facility_id: string;
  if_multi_regimen: boolean;
  payer_patterns: string[];
  provider_id: string;
  provider_name: string;
  working_today: boolean;
  medical_order_status?: string[]; // Array of: "order_completed_by_agent", "order_in_progress", "yet_to_start_work_on_order"
  [key: string]: any;
}

export class OrderAssignmentConfigService {
  /**
   * Fetch all order assignment configurations for a specific facility
   * @param facilityId - The facility ID to filter configurations by
   * @returns Promise<OrderAssignmentConfiguration[]> - Array of configurations matching the facility_id
   */
  static async getConfigurationsByFacilityId(
    facilityId: string,
  ): Promise<OrderAssignmentConfiguration[]> {
    try {
      const collectionPath =
        FirestoreCollectionReference.orderAssignmentConfigurations();
      const queryConstraints: QueryConstraint[] = [
        where("facility_id", "==", facilityId),
      ];

      const configurations =
        await FirestoreService.getDocumentsByQuery<OrderAssignmentConfiguration>(
          collectionPath,
          queryConstraints,
        );

      return configurations;
    } catch (error) {
      logError(
        error as Error,
        `Error fetching order assignment configurations for facility ${facilityId}`,
      );
      throw error;
    }
  }

  /**
   * Fetch all order assignment configurations (no filter)
   * @returns Promise<OrderAssignmentConfiguration[]> - Array of all configurations
   */
  static async getAllConfigurations(): Promise<OrderAssignmentConfiguration[]> {
    try {
      const collectionPath =
        FirestoreCollectionReference.orderAssignmentConfigurations();
      const configurations =
        await FirestoreService.getAllDocuments<OrderAssignmentConfiguration>(
          collectionPath,
        );

      return configurations;
    } catch (error) {
      logError(
        error as Error,
        "Error fetching all order assignment configurations",
      );
      throw error;
    }
  }

  /**
   * Update one or more fields of a specific configuration
   * @param configId - The configuration document ID
   * @param fieldsToUpdate - Partial object containing the fields to update
   * @returns Promise<void>
   */
  static async updateConfiguration(
    configId: string,
    fieldsToUpdate: Partial<OrderAssignmentConfiguration>,
  ): Promise<void> {
    try {
      const collectionPath =
        FirestoreCollectionReference.orderAssignmentConfigurations();
      await FirestoreService.updateDocument(
        collectionPath,
        configId,
        fieldsToUpdate,
      );
    } catch (error) {
      logError(error as Error, `Error updating configuration ${configId}`);
      throw error;
    }
  }

  /**
   * Create a new order assignment configuration
   * @param config - The configuration object to create
   * @returns Promise<OrderAssignmentConfiguration> - The created configuration with ID
   */
  static async createConfiguration(
    config: Omit<OrderAssignmentConfiguration, "id">,
  ): Promise<OrderAssignmentConfiguration> {
    try {
      const collectionPath =
        FirestoreCollectionReference.orderAssignmentConfigurations();
      const createdConfig =
        await FirestoreService.addDocument<OrderAssignmentConfiguration>(
          collectionPath,
          config as any,
        );
      return createdConfig;
    } catch (error) {
      logError(error as Error, "Error creating order assignment configuration");
      throw error;
    }
  }

  /**
   * Retrieves order assignment configurations matching a specific facility
   * and authorization status.
   *
   * @param facilityId - The healthcare facility identifier to filter by.
   * @param authStatus - The authorization status value; matched via
   *   Firestore `array-contains` against the document's `auth_status` array.
   * @returns Configurations that belong to the given facility and include
   *   the specified auth status.
   * @throws Re-throws the original Firestore error after logging.
   */
  static async getConfigurationsByFacilityIdAndAuthStatus(
    facilityId: string,
    authStatus: string,
  ): Promise<OrderAssignmentConfiguration[]> {
    try {
      const collectionPath =
        FirestoreCollectionReference.orderAssignmentConfigurations();
      const queryConstraints: QueryConstraint[] = [
        where("facility_id", "==", facilityId),
        where("auth_status", "array-contains", authStatus),
      ];
      const configurations =
        await FirestoreService.getDocumentsByQuery<OrderAssignmentConfiguration>(
          collectionPath,
          queryConstraints,
        );
      return configurations;
    } catch (error) {
      logError(
        error as Error,
        "Error fetching order assignment configuration by facility ID and auth status",
      );
      throw error;
    }
  }
}
